import { Link, useLocation, useNavigate } from "react-router-dom";
import { Inbox, FileText, LogOut, RefreshCcw, Building2, Users } from "lucide-react";
import Logo from "@/components/Logo";
import { clearAdminToken } from "@/lib/api";

/**
 * Shared header for /admin/* pages. Provides brand, cross-page nav,
 * refresh + sign-out actions.
 */
export default function AdminHeader({ onRefresh, refreshing = false, active }) {
  const navigate = useNavigate();
  const location = useLocation();
  const current = active || (
    location.pathname.startsWith("/admin/briefs") ? "briefs"
    : location.pathname.startsWith("/admin/businesses") ? "businesses"
    : location.pathname.startsWith("/admin/customers") ? "customers"
    : "requests"
  );

  const signOut = () => {
    clearAdminToken();
    navigate("/admin/requests");
  };

  return (
    <header
      className="sticky top-0 z-30 backdrop-blur-md border-b"
      style={{ background: "rgba(249,248,246,0.9)", borderColor: "var(--ta-border)" }}
    >
      <div className="w-full max-w-screen-lg mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
          <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
            Admin
          </span>
        </div>

        <nav className="hidden sm:flex items-center gap-1 rounded-full p-1" style={{ background: "var(--ta-surface-alt)" }} data-testid="admin-nav">
          <NavPill to="/admin/briefs" icon={FileText} label="Briefs" active={current === "briefs"} testId="admin-nav-briefs" />
          <NavPill to="/admin/businesses" icon={Building2} label="Businesses" active={current === "businesses"} testId="admin-nav-businesses" />
          <NavPill to="/admin/customers" icon={Users} label="Customers" active={current === "customers"} testId="admin-nav-customers" />
          <NavPill to="/admin/requests" icon={Inbox} label="Requests" active={current === "requests"} testId="admin-nav-requests" />
        </nav>

        <div className="flex items-center gap-1">
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={refreshing}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-1 h-10 px-3 rounded-full text-sm font-medium disabled:opacity-50"
              style={{ color: "var(--ta-primary)" }}
            >
              <RefreshCcw size={16} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
          )}
          <button
            onClick={signOut}
            data-testid="admin-signout"
            className="inline-flex items-center gap-1 h-10 px-3 rounded-full text-sm font-medium"
            style={{ color: "var(--ta-primary)" }}
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      <nav className="sm:hidden w-full max-w-screen-lg mx-auto px-5 pb-3 flex gap-1" data-testid="admin-nav-mobile">
        <NavPill to="/admin/briefs" icon={FileText} label="Briefs" active={current === "briefs"} testId="admin-nav-briefs-m" grow />
        <NavPill to="/admin/businesses" icon={Building2} label="Shops" active={current === "businesses"} testId="admin-nav-businesses-m" grow />
        <NavPill to="/admin/customers" icon={Users} label="Customers" active={current === "customers"} testId="admin-nav-customers-m" grow />
        <NavPill to="/admin/requests" icon={Inbox} label="Requests" active={current === "requests"} testId="admin-nav-requests-m" grow />
      </nav>
    </header>
  );
}

const NavPill = ({ to, icon: Icon, label, active, testId, grow = false }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium transition-colors ${grow ? "flex-1" : ""}`}
    style={{
      background: active ? "var(--ta-primary)" : "transparent",
      color: active ? "#fff" : "var(--ta-primary)",
    }}
  >
    <Icon size={15} strokeWidth={2.2} /> {label}
  </Link>
);
